import React from 'react';
import type { ClientInvoiceViewModel } from '../../lib/clientInvoiceTemplate';
import {
  formatInvoiceAmount,
  formatInvoiceNzd,
  formatInvoiceQty,
  INVOICE_GST_PERCENT_LABEL,
} from '../../lib/clientInvoiceTemplate';

interface ClientInvoiceDocumentBodyProps {
  viewModel: ClientInvoiceViewModel;
}

const TABLE_HEAD = 'bg-[#3d3d3d] text-white';

const ClientInvoiceDocumentBody: React.FC<ClientInvoiceDocumentBodyProps> = ({ viewModel: vm }) => {
  return (
    <div className="text-sm text-gray-900">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <p className="text-base font-bold text-gray-900">{vm.clientName}</p>
        <div className="space-y-0.5 text-right text-sm text-gray-800">
          <p>
            <span className="text-gray-600">Invoice Date :</span> {vm.invoiceDate}
          </p>
          <p>
            <span className="text-gray-600">Terms :</span> {vm.paymentTerms}
          </p>
          {vm.dueDate ? (
            <p>
              <span className="text-gray-600">Due Date :</span> {vm.dueDate}
            </p>
          ) : null}
          {vm.periodLabel ? (
            <p>
              <span className="text-gray-600">Period :</span> {vm.periodLabel}
            </p>
          ) : null}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr className={TABLE_HEAD}>
              <th className="px-3 py-2.5 text-center font-semibold">#</th>
              <th className="px-3 py-2.5 text-left font-semibold">Description</th>
              <th className="px-3 py-2.5 text-right font-semibold">Qty</th>
              <th className="px-3 py-2.5 text-right font-semibold">Rate</th>
              <th className="px-3 py-2.5 text-right font-semibold">GST</th>
              <th className="px-3 py-2.5 text-right font-semibold">Amount</th>
            </tr>
          </thead>
          <tbody>
            {vm.lines.map((line) => (
              <tr key={line.lineNumber} className="border-b border-gray-200">
                <td className="px-3 py-3 text-center text-gray-700">{line.lineNumber}</td>
                <td className="px-3 py-3 text-gray-900">{line.description}</td>
                <td className="px-3 py-3 text-right text-gray-800">{formatInvoiceQty(line.quantity)}</td>
                <td className="px-3 py-3 text-right text-gray-800">{formatInvoiceAmount(line.rate)}</td>
                <td className="px-3 py-3 text-right text-gray-800">
                  <div>{formatInvoiceAmount(line.gst)}</div>
                  <div className="text-xs text-gray-500">{INVOICE_GST_PERCENT_LABEL}</div>
                </td>
                <td className="px-3 py-3 text-right text-gray-900">{formatInvoiceAmount(line.amount)}</td>
              </tr>
            ))}
            <tr className="border-b border-gray-300">
              <td colSpan={3} className="px-3 py-2" />
              <td className="px-3 py-2 text-right font-semibold text-gray-900">Sub Total</td>
              <td className="px-3 py-2 text-right text-gray-900">{formatInvoiceAmount(vm.gstAmount)}</td>
              <td className="px-3 py-2 text-right text-gray-900">{formatInvoiceAmount(vm.totalIncGst)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="mt-1 flex justify-end">
        <div className="w-full max-w-md text-sm">
          <div className="flex justify-between border-b border-gray-200 py-2">
            <span className="text-gray-700">Total Taxable Amount</span>
            <span className="text-gray-900">{formatInvoiceAmount(vm.subtotalExGst)}</span>
          </div>
          <div className="flex justify-between border-b border-gray-200 py-2">
            <span className="font-semibold text-gray-900">Total</span>
            <span className="font-bold text-gray-900">{formatInvoiceNzd(vm.totalIncGst)}</span>
          </div>
          <div className="flex justify-between bg-gray-100 px-3 py-2.5">
            <span className="font-semibold text-gray-900">Balance Due</span>
            <span className="font-bold text-gray-900">{formatInvoiceNzd(vm.balanceDue)}</span>
          </div>
        </div>
      </div>

      <div className="mt-8">
        <p className="mb-2 font-medium text-gray-900">Tax Summary</p>
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr className={TABLE_HEAD}>
              <th className="px-3 py-2.5 text-left font-semibold">Tax Details</th>
              <th className="px-3 py-2.5 text-right font-semibold">Taxable Amount (NZD)</th>
              <th className="px-3 py-2.5 text-right font-semibold">Tax Amount (NZD)</th>
              <th className="px-3 py-2.5 text-right font-semibold">Total Amount (NZD)</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-200">
              <td className="px-3 py-2.5 text-gray-900">GST (15%)</td>
              <td className="px-3 py-2.5 text-right text-gray-800">
                {formatInvoiceAmount(vm.subtotalExGst)}
              </td>
              <td className="px-3 py-2.5 text-right text-gray-800">
                {formatInvoiceAmount(vm.gstAmount)}
              </td>
              <td className="px-3 py-2.5 text-right text-gray-800">
                {formatInvoiceAmount(vm.totalIncGst)}
              </td>
            </tr>
            <tr className="font-bold text-gray-900">
              <td className="px-3 py-2.5">Total</td>
              <td className="px-3 py-2.5 text-right">{formatInvoiceNzd(vm.subtotalExGst)}</td>
              <td className="px-3 py-2.5 text-right">{formatInvoiceNzd(vm.gstAmount)}</td>
              <td className="px-3 py-2.5 text-right">{formatInvoiceNzd(vm.totalIncGst)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p className="mt-8 text-sm text-gray-800">Thanks for your business.</p>

      {vm.bankAccountNumber || vm.bankAccountName ? (
        <div className="mt-6 space-y-1 text-sm text-gray-800">
          {vm.bankAccountNumber ? <p>{vm.bankAccountNumber}</p> : null}
          {vm.bankAccountName ? <p>{vm.bankAccountName}</p> : null}
          <p className="text-gray-600">Reference: Invoice Number</p>
        </div>
      ) : null}

      {vm.notes ? (
        <p className="mt-4 text-sm text-gray-600">
          <span className="font-medium text-gray-800">Notes:</span> {vm.notes}
        </p>
      ) : null}
    </div>
  );
};

export default ClientInvoiceDocumentBody;
